/**
 * Page backoffice : gestion des demandes d'activation à distance.
 * Accessible aux super_admin, admin, agent, agent_caisse.
 * Charte sobre cohérente avec le backoffice.
 */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  Inbox, Search, CheckCircle2, XCircle, AlertCircle, Loader2, Image as ImageIcon,
  Clock, Eye, Wallet,
} from 'lucide-react';
import {
  useGetDemandesActivationQuery,
  useValiderDemandeMutation,
  useAnnulerValidationDemandeMutation,
  useRejeterDemandeMutation,
  useDemanderComplementMutation,
  DemandeActivation, StatutDemande,
} from '../../services/demandesActivationApi';
import { ImageLightbox } from '../../components/ImageLightbox';
import { EtatPaiementModal, EtudiantPaiementRef } from '../PortailAdmin/EtatPaiementModal';

const PERIODE_LABELS: Record<string, string> = {
  INSC: 'Inscription / Réinscription', NOV: 'Novembre', DEC: 'Décembre', JAN: 'Janvier',
  FEV: 'Février', MAR: 'Mars', AVR: 'Avril', MAI: 'Mai',
  JUIN: 'Juin', JUIL: 'Juillet', AOUT: 'Août', EXAM: 'Examen',
};

// Année académique : novembre → août + inscription + examen (2 semestres).
const PERIODE_CODES = ['INSC','NOV','DEC','JAN','FEV','MAR','AVR','MAI','JUIN','JUIL','AOUT','EXAM'];

const TYPE_LABELS: Record<string, string> = {
  paiement: 'Paiement', gratuite: 'Gratuité', bourse: 'Bourse', autre: 'Autre',
};

const STATUTS: { key: 'a_traiter' | StatutDemande | 'all'; label: string }[] = [
  { key: 'a_traiter', label: 'À traiter' },
  { key: 'en_attente', label: 'En attente' },
  { key: 'complement_demande', label: 'Complément demandé' },
  { key: 'acceptee', label: 'Acceptées' },
  { key: 'rejetee', label: 'Rejetées' },
  { key: 'cloturee', label: 'Clôturées' },
  { key: 'all', label: 'Toutes' },
];

const STATUT_BADGE: Record<string, string> = {
  en_attente: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  complement_demande: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  acceptee: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  rejetee: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  cloturee: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

const DemandesActivationPage: React.FC = () => {
  const user = useSelector((s: RootState) => s.auth.user);
  const allowed = ['super_admin', 'admin', 'agent', 'agent_caisse'].includes(user?.role || '');

  const [statut, setStatut] = useState<'a_traiter' | StatutDemande | 'all'>('a_traiter');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<DemandeActivation | null>(null);
  // Filtre : masquer les demandes dont les périodes sont déjà couvertes par un
  // import xlsx postérieur (la caisse n'a plus besoin de les traiter).
  const [masquerCouvertes, setMasquerCouvertes] = useState(false);

  // Formulaire validation
  const [periodesValidees, setPeriodesValidees] = useState<string[]>([]);
  const [montantValide, setMontantValide] = useState('');
  const [messageCaisse, setMessageCaisse] = useState('');
  const [motifRejet, setMotifRejet] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; label: string } | null>(null);
  // État de paiement complet + accès léger individuel (2026-09-13)
  const [paiementEtudiant, setPaiementEtudiant] = useState<EtudiantPaiementRef | null>(null);

  const { data, isLoading, refetch } = useGetDemandesActivationQuery({ statut, q: q || undefined, limit: 200 });
  const [validerMutation, { isLoading: validating }] = useValiderDemandeMutation();
  const [annulerValidationMutation, { isLoading: annulating }] = useAnnulerValidationDemandeMutation();
  const [rejeterMutation, { isLoading: rejecting }] = useRejeterDemandeMutation();
  const [complementMutation, { isLoading: askingComplement }] = useDemanderComplementMutation();

  const itemsRaw = data?.results || [];
  const items = useMemo(() => {
    if (!masquerCouvertes) return itemsRaw;
    return itemsRaw.filter(d => !d.paiement_courant?.demande_couverte);
  }, [itemsRaw, masquerCouvertes]);
  const nbCouvertes = useMemo(
    () => itemsRaw.filter(d => d.paiement_courant?.demande_couverte).length,
    [itemsRaw],
  );

  const handleSelect = (d: DemandeActivation) => {
    setSelected(d);
    setPeriodesValidees(d.periodes_validees?.length ? d.periodes_validees : (d.periodes_concernees || []));
    setMontantValide(d.montant_valide || d.montant_declare || '');
    setMessageCaisse('');
    setMotifRejet('');
    setFeedback(null);
  };

  const togglePeriode = (code: string) => {
    setPeriodesValidees((prev) => prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code]);
  };

  const handleValider = async () => {
    if (!selected) return;
    if (selected.type_demande === 'paiement' && periodesValidees.length === 0) {
      setFeedback({ type: 'error', text: 'Sélectionnez au moins une période à valider.' });
      return;
    }
    try {
      await validerMutation({
        id: selected.id,
        montant_valide: montantValide.trim() || undefined,
        periodes_validees: periodesValidees,
        message_caisse: messageCaisse.trim() || undefined,
      }).unwrap();
      setFeedback({ type: 'success', text: 'Demande validée. L\'étudiant sera notifié.' });
      refetch();
      setSelected(null);
    } catch (e: any) {
      setFeedback({ type: 'error', text: e?.data?.detail || 'Erreur lors de la validation' });
    }
  };

  const handleRejeter = async () => {
    if (!selected) return;
    if (motifRejet.trim().length < 5) {
      setFeedback({ type: 'error', text: 'Motif de rejet requis (5 caractères min).' });
      return;
    }
    try {
      await rejeterMutation({
        id: selected.id,
        motif_rejet: motifRejet.trim(),
        message_caisse: messageCaisse.trim() || undefined,
      }).unwrap();
      setFeedback({ type: 'success', text: 'Demande rejetée. L\'étudiant sera notifié.' });
      refetch();
      setSelected(null);
    } catch (e: any) {
      setFeedback({ type: 'error', text: e?.data?.detail || 'Erreur lors du rejet' });
    }
  };

  const handleComplement = async () => {
    if (!selected) return;
    if (messageCaisse.trim().length < 5) {
      setFeedback({ type: 'error', text: 'Expliquez ce qui manque (5 caractères min).' });
      return;
    }
    try {
      await complementMutation({
        id: selected.id,
        message_caisse: messageCaisse.trim(),
      }).unwrap();
      setFeedback({ type: 'success', text: 'Demande renvoyée à l\'étudiant pour complément.' });
      refetch();
      setSelected(null);
    } catch (e: any) {
      setFeedback({ type: 'error', text: e?.data?.detail || 'Erreur' });
    }
  };

  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <Inbox className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Accès restreint</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Cette page est réservée à la caisse / scolarité.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Inbox size={22} className="text-blue-600 dark:text-blue-400" />
          Demandes d'activation
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Validation des reçus, gratuités et bourses soumis par les étudiants depuis le portail.
        </p>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg">
          {STATUTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatut(s.key)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                statut === s.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {s.label}
              {s.key === 'a_traiter' && data && data.nb_a_traiter > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold bg-orange-600 text-white rounded-full px-1">
                  {data.nb_a_traiter}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[180px] relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (matricule, nom, n° reçu)..."
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        {nbCouvertes > 0 && (
          <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-900/15 text-xs text-emerald-700 dark:text-emerald-300 cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/30">
            <input type="checkbox"
              checked={masquerCouvertes}
              onChange={(e) => setMasquerCouvertes(e.target.checked)}
              className="rounded" />
            Masquer les {nbCouvertes} déjà à jour
          </label>
        )}
      </div>

      {['a_traiter', 'en_attente', 'complement_demande'].includes(statut) && (data?.nb_ecartees_a_jour || 0) > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {data!.nb_ecartees_a_jour} demande{(data!.nb_ecartees_a_jour || 0) > 1 ? 's' : ''} écartée{(data!.nb_ecartees_a_jour || 0) > 1 ? 's' : ''} :
          étudiant{(data!.nb_ecartees_a_jour || 0) > 1 ? 's' : ''} déjà à jour de paiement d'après le fichier de la caisse.
          Consultables dans « Toutes ».
        </div>
      )}

      {feedback && (
        <div className={`p-3 rounded text-xs border ${
          feedback.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
        }`}>{feedback.text}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(360px,2fr)_3fr] gap-3">
        {/* Liste */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
          {isLoading ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              <Loader2 className="inline-block animate-spin mr-2" size={14} /> Chargement...
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center">
              <Inbox className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-400">Aucune demande</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700/60 max-h-[70vh] overflow-y-auto">
              {items.map((d) => (
                <button
                  key={d.id}
                  onClick={() => handleSelect(d)}
                  className={`w-full text-left p-3 transition-colors ${
                    selected?.id === d.id
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                      {d.nom_complet || d.matricule}
                    </div>
                    <div className="text-[10px] text-gray-400 flex-shrink-0">
                      {new Date(d.date_creation).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 font-mono">
                    {d.matricule}{d.classe ? ` • ${d.classe}` : ''}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUT_BADGE[d.statut]}`}>
                      {d.statut === 'en_attente' && <Clock size={10} className="inline mr-0.5" />}
                      {d.statut === 'complement_demande' && <AlertCircle size={10} className="inline mr-0.5" />}
                      {d.statut === 'acceptee' && <CheckCircle2 size={10} className="inline mr-0.5" />}
                      {d.statut === 'rejetee' && <XCircle size={10} className="inline mr-0.5" />}
                      {d.statut === 'cloturee' && <CheckCircle2 size={10} className="inline mr-0.5" />}
                      {d.statut === 'en_attente' ? 'En attente' :
                       d.statut === 'complement_demande' ? 'Complément' :
                       d.statut === 'acceptee' ? 'Acceptée' :
                       d.statut === 'cloturee' ? 'Clôturée' : 'Rejetée'}
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {TYPE_LABELS[d.type_demande]}
                    </span>
                    {d.montant_declare && (
                      <span className="text-[10px] text-gray-700 dark:text-gray-300 font-mono">
                        {parseFloat(d.montant_declare).toLocaleString('fr-FR')} F
                      </span>
                    )}
                    {/* Badge "déjà à jour" : la demande est devenue caduque après
                        l'import xlsx de la caisse. La caisse peut directement
                        rejeter/accepter sans re-vérifier. */}
                    {d.paiement_courant?.demande_couverte && (
                      <span className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                        title="Toutes les périodes de cette demande sont déjà à jour suite à un import xlsx">
                        <CheckCircle2 size={9} /> Déjà à jour
                      </span>
                    )}
                    {d.etudiant_a_jour && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                        title="Étudiant à jour de toutes les périodes requises : demande écartée des listes à traiter">
                        Étudiant à jour
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Détail */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
          {!selected ? (
            <div className="text-sm text-gray-400 text-center py-12">
              Sélectionnez une demande à gauche
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header étudiant */}
              <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {selected.nom_complet || selected.matricule}
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${STATUT_BADGE[selected.statut]}`}>
                    {selected.statut === 'en_attente' ? 'En attente' :
                     selected.statut === 'complement_demande' ? 'Complément demandé' :
                     selected.statut === 'acceptee' ? 'Acceptée' :
                     selected.statut === 'cloturee' ? 'Clôturée (étudiant à jour)' : 'Rejetée'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  {selected.matricule}{selected.classe ? ` • ${selected.classe}` : ''}{selected.niveau ? ` • ${selected.niveau}` : ''}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Soumise le {new Date(selected.date_creation).toLocaleString('fr-FR')}
                </div>
                <button
                  type="button"
                  onClick={() => setPaiementEtudiant({
                    matricule: selected.matricule,
                    nom: selected.nom_complet,
                    classe: selected.classe,
                  })}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Wallet size={13} /> État de paiement et accès léger
                </button>
              </div>

              {/* Bannière paiement courant : la caisse voit immédiatement si
                  l'étudiant est déjà à jour suite à un import xlsx récent. */}
              {selected.paiement_courant?.connu && (
                <div className={`p-3 rounded-lg border text-sm ${
                  selected.paiement_courant.demande_couverte
                    ? 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
                    : selected.paiement_courant.periodes_a_jour.length > 0
                      ? 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200'
                      : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                }`}>
                  <div className="flex items-start gap-2">
                    {selected.paiement_courant.demande_couverte ? (
                      <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      {selected.paiement_courant.demande_couverte ? (
                        <>
                          <div className="font-semibold">
                            Étudiant déjà à jour pour toutes les périodes demandées
                          </div>
                          <div className="text-xs mt-0.5">
                            Les paiements pour <strong>{(selected.periodes_concernees || []).map(p => PERIODE_LABELS[p] || p).join(', ')}</strong> ont été enregistrés (probablement par import xlsx).
                            Vous pouvez archiver/accepter cette demande directement.
                          </div>
                        </>
                      ) : selected.paiement_courant.periodes_a_jour.length > 0 ? (
                        <>
                          <div className="font-semibold">Paiement partiel détecté</div>
                          <div className="text-xs mt-0.5">
                            À jour : <strong>{selected.paiement_courant.periodes_a_jour.map(p => PERIODE_LABELS[p] || p).join(', ')}</strong><br />
                            Encore dû : <strong>{selected.paiement_courant.periodes_en_dette.map(p => PERIODE_LABELS[p] || p).join(', ') || '—'}</strong>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold">Aucun paiement à jour</div>
                          <div className="text-xs mt-0.5">
                            Aucune période n'est marquée à jour. La caisse doit traiter cette demande.
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Photos — clic ouvre le lightbox inline (pas de nouvel onglet) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {selected.photo_url && (
                  <button type="button"
                    onClick={() => setLightbox({ src: selected.photo_url!, label: 'Reçu original' })}
                    className="block text-left border border-gray-200 dark:border-gray-700 rounded overflow-hidden hover:border-blue-500 dark:hover:border-blue-500 transition-colors">
                    <div className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      <ImageIcon size={10} /> Reçu original — cliquer pour agrandir
                    </div>
                    <img src={selected.photo_url} alt="Reçu" className="w-full max-h-72 object-contain cursor-zoom-in" />
                  </button>
                )}
                {selected.photo_complement_url && (
                  <button type="button"
                    onClick={() => setLightbox({ src: selected.photo_complement_url!, label: 'Complément' })}
                    className="block text-left border border-blue-200 dark:border-blue-700 rounded overflow-hidden hover:border-blue-500 transition-colors">
                    <div className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-[10px] text-blue-700 dark:text-blue-300 flex items-center gap-1">
                      <ImageIcon size={10} /> Complément — cliquer pour agrandir
                    </div>
                    <img src={selected.photo_complement_url} alt="Complément" className="w-full max-h-72 object-contain cursor-zoom-in" />
                  </button>
                )}
              </div>

              {/* Infos */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Type :</span>{' '}
                  <span className="text-gray-900 dark:text-gray-100">{TYPE_LABELS[selected.type_demande]}</span>
                </div>
                {selected.montant_declare && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Montant déclaré :</span>{' '}
                    <span className="text-gray-900 dark:text-gray-100 font-mono">{selected.montant_declare} FCFA</span>
                  </div>
                )}
                {selected.mode_paiement && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Mode :</span>{' '}
                    <span className="text-gray-900 dark:text-gray-100">{selected.mode_paiement}</span>
                  </div>
                )}
                {selected.reference_recu && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">N° reçu :</span>{' '}
                    <span className="text-gray-900 dark:text-gray-100 font-mono">{selected.reference_recu}</span>
                  </div>
                )}
                {selected.date_paiement_declaree && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Date paiement :</span>{' '}
                    <span className="text-gray-900 dark:text-gray-100">{selected.date_paiement_declaree}</span>
                  </div>
                )}
              </div>

              {selected.message_etudiant && (
                <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded p-3 text-xs">
                  <div className="text-gray-500 dark:text-gray-400 mb-1">Message :</div>
                  <div className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{selected.message_etudiant}</div>
                </div>
              )}

              {/* Validation form (uniquement si pas déjà traitée définitivement) */}
              {selected.statut !== 'acceptee' && selected.statut !== 'rejetee' && selected.statut !== 'cloturee' && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      Périodes à valider
                    </label>
                    <div className="grid grid-cols-5 gap-1.5">
                      {PERIODE_CODES.map((c) => {
                        const sel = periodesValidees.includes(c);
                        return (
                          <button
                            key={c}
                            onClick={() => togglePeriode(c)}
                            className={`px-2 py-1 text-[10px] rounded border transition-colors ${
                              sel
                                ? 'bg-blue-600 border-blue-600 text-white font-medium'
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {PERIODE_LABELS[c]}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                        Montant validé (FCFA)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={montantValide}
                        onChange={(e) => setMontantValide(e.target.value)}
                        placeholder={selected.montant_declare || '0'}
                        className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      Message à l'étudiant (facultatif)
                    </label>
                    <textarea
                      value={messageCaisse}
                      onChange={(e) => setMessageCaisse(e.target.value)}
                      rows={2}
                      placeholder="Ex: Bien reçu, votre compte est activé."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      Motif de rejet (si rejet)
                    </label>
                    <textarea
                      value={motifRejet}
                      onChange={(e) => setMotifRejet(e.target.value)}
                      rows={2}
                      placeholder="Ex: Reçu illisible, montant ne correspond pas, etc."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
                    <button
                      onClick={handleComplement}
                      disabled={askingComplement}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-medium rounded transition-colors"
                    >
                      <AlertCircle size={13} /> Demander complément
                    </button>
                    <button
                      onClick={handleRejeter}
                      disabled={rejecting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 text-xs font-medium rounded transition-colors"
                    >
                      <XCircle size={13} /> Rejeter
                    </button>
                    <button
                      onClick={handleValider}
                      disabled={validating}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors"
                    >
                      {validating ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                      Valider et activer
                    </button>
                  </div>
                </div>
              )}

              {/* État de traitement (si déjà traitée) */}
              {(selected.statut === 'acceptee' || selected.statut === 'rejetee' || selected.statut === 'cloturee') && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700 text-xs space-y-1">
                  <div className="text-gray-500 dark:text-gray-400">
                    Traitée par <strong>{selected.traite_par_nom || '—'}</strong>{' '}
                    {selected.date_traitement && (
                      <>le {new Date(selected.date_traitement).toLocaleString('fr-FR')}</>
                    )}
                  </div>
                  {selected.statut === 'acceptee' && selected.periodes_validees?.length > 0 && (
                    <div className="text-green-700 dark:text-green-400">
                      Périodes validées : {selected.periodes_validees.map((p) => PERIODE_LABELS[p] || p).join(', ')}
                    </div>
                  )}
                  {selected.motif_rejet && (
                    <div className="text-red-700 dark:text-red-400">Motif : {selected.motif_rejet}</div>
                  )}
                  {selected.message_caisse && (
                    <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">Message : {selected.message_caisse}</div>
                  )}

                  {/* Bouton Annuler la validation (si validée par erreur) */}
                  {selected.statut === 'acceptee' && (
                    <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={async () => {
                          const motif = window.prompt(
                            'Annuler la validation de cette demande ?\n\n'
                            + 'La demande repassera "en attente" pour être ré-examinée. '
                            + 'Les périodes marquées "à jour" via cette demande seront marquées comme "non à jour" '
                            + '(audit conservé dans le motif).\n\n'
                            + 'Motif de l\'annulation (5 caractères min, communiqué à l\'étudiant) :'
                          );
                          if (motif === null) return;
                          if (motif.trim().length < 5) {
                            alert('Motif requis (5 caractères minimum)');
                            return;
                          }
                          try {
                            const res = await annulerValidationMutation({ id: selected.id, motif: motif.trim() }).unwrap();
                            alert(`Validation annulée. ${res.nb_etats_modifies} état(s) de paiement remis "non à jour".`);
                          } catch (e: any) {
                            alert(e?.data?.detail || 'Erreur');
                          }
                        }}
                        disabled={annulating}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/50 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800 rounded font-medium disabled:opacity-50"
                        title="Si la validation a été faite par erreur (reçu non conforme...), elle peut être annulée."
                      >
                        ↩ {annulating ? 'Annulation…' : 'Annuler cette validation'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {paiementEtudiant && (
        <EtatPaiementModal
          etudiant={paiementEtudiant}
          onClose={() => { setPaiementEtudiant(null); refetch(); }}
        />
      )}

      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.label}
          filename={lightbox.label}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

export default DemandesActivationPage;
